"use client";

/**
 * QuarterForm — bespoke add/edit form for Quarter Management.
 * Replaces GenericFormPage to support RichHtmlEditor for required description
 * authoring, matching the Angular quarter form behavior.
 *
 * Add:  POST quatermanagement/add-edit  { quarter_name, sit_count, priority, description, status, start_date?, end_date? }
 * Edit: POST quatermanagement/add-edit  { ...same, _id }
 * Fetch (edit): POST quatermanagement/quarter-fetch { _id }
 */

import { useRouter, useParams } from "next/navigation";
import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { useSession } from "next-auth/react";
import { useQuery } from "@tanstack/react-query";
import { useApiPost } from "@admin/_lib/api-hooks";
import { RichHtmlEditor } from "@admin/_components/rich-html-editor";

// ── Validation schema ─────────────────────────────────────────────────────────

const quarterSchema = z.object({
  quarter_name: z.string().min(1, "Quarter name is required"),
  sit_count: z.number().min(1, "Sit count must be at least 1"),
  priority: z.number().min(0, "Priority must be 0 or greater"),
  start_date: z.string().optional(),
  end_date: z.string().optional(),
  description: z.string().min(1, "Description is required"),
  status: z.boolean(),
});

type QuarterValues = z.infer<typeof quarterSchema>;

// ── Component ─────────────────────────────────────────────────────────────────

function getBaseUrl() {
  return process.env.NEXT_PUBLIC_API_CLOUDFRONT ?? "https://api.divineinfinitebeing.com/";
}

export function QuarterForm({ mode }: { mode: "add" | "edit" }) {
  const router = useRouter();
  const params = useParams();
  const quarterId = mode === "edit" ? (params?.id as string) : undefined;
  const { data: session } = useSession();

  // ── Edit fetch ────────────────────────────────────────────────────────────

  const { data: existing, isLoading: fetchLoading } = useQuery<Record<string, unknown>>({
    queryKey: ["quarter", quarterId],
    queryFn: async () => {
      const res = await fetch(`${getBaseUrl()}quatermanagement/quarter-fetch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.token ?? ""}`,
        },
        body: JSON.stringify({ _id: quarterId }),
      });
      const data = await res.json();
      return data.results?.res ?? data.results ?? data;
    },
    enabled: mode === "edit" && !!quarterId && !!session?.token,
  });

  // ── Form ──────────────────────────────────────────────────────────────────

  const form = useForm<QuarterValues>({
    resolver: zodResolver(quarterSchema),
    defaultValues: {
      quarter_name: "",
      sit_count: 0,
      priority: 0,
      start_date: "",
      end_date: "",
      description: "",
      status: true,
    },
  });

  const { reset } = form;

  useEffect(() => {
    if (existing) {
      reset({
        quarter_name: (existing.quarter_name as string) ?? "",
        sit_count: Number(existing.sit_count ?? 0),
        priority: Number(existing.priority ?? 0),
        start_date: existing.start_date ? String(existing.start_date).slice(0, 10) : "",
        end_date: existing.end_date ? String(existing.end_date).slice(0, 10) : "",
        description: (existing.description as string) ?? "",
        status: existing.status === 1 || existing.status === true,
      });
    }
  }, [existing, reset]);

  // ── Mutations ─────────────────────────────────────────────────────────────

  const mutation = useApiPost("quatermanagement/add-edit", {
    invalidateKeys: ["quatermanagement/quater-list", "quatermanagement/quater-list-count"],
  });

  async function onSubmit(values: QuarterValues) {
    try {
      const payload: Record<string, unknown> = {
        quarter_name: values.quarter_name,
        sit_count: values.sit_count,
        priority: values.priority,
        description: values.description,
        status: values.status ? 1 : 0,
      };
      if (values.start_date) payload.start_date = values.start_date;
      if (values.end_date) payload.end_date = values.end_date;
      if (mode === "edit" && quarterId) payload._id = quarterId;

      await mutation.mutateAsync(payload);
      toast.success(mode === "edit" ? "Quarter updated" : "Quarter created");
      router.push("/admin/quarters");
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to save quarter");
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  const isPageLoading = mode === "edit" && fetchLoading;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div
          className="h-7 w-1 rounded-full shrink-0"
          style={{ background: "linear-gradient(to bottom, #ffa800, #ff7e00)" }}
        />
        <div className="flex-1">
          <h1 className="text-xl font-semibold text-foreground">
            {mode === "edit" ? "Edit Quarter" : "Add Quarter"}
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Configure a training quarter with schedule dates and seat count
          </p>
        </div>
        <Button variant="ghost" size="icon" onClick={() => router.back()}>
          <ArrowLeft className="size-4" />
        </Button>
      </div>

      {isPageLoading ? (
        <div className="space-y-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-10 w-full rounded-lg" />
          ))}
        </div>
      ) : (
        <Card style={{ borderColor: "hsl(240 20% 22%)" }}>
          <CardHeader className="border-b py-3 px-5" style={{ borderColor: "hsl(240 20% 20%)" }}>
            <CardTitle className="flex items-center gap-2 text-base">
              <span className="h-4 w-0.5 rounded-full" style={{ background: "#ffa800" }} />
              Quarter Details
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2">
                  {/* Quarter Name */}
                  <FormField
                    control={form.control}
                    name="quarter_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Quarter Name <span className="text-destructive">*</span></FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. Quarter 1 2026" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Sit Count */}
                  <FormField
                    control={form.control}
                    name="sit_count"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Sit Count <span className="text-destructive">*</span></FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min={1}
                            placeholder="e.g. 30"
                            value={field.value ?? ""}
                            onChange={(e) => field.onChange(e.target.value === "" ? undefined : Number(e.target.value))}
                            onBlur={field.onBlur}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Priority */}
                  <FormField
                    control={form.control}
                    name="priority"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Priority <span className="text-destructive">*</span></FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min={0}
                            placeholder="e.g. 1"
                            value={field.value ?? ""}
                            onChange={(e) => field.onChange(e.target.value === "" ? undefined : Number(e.target.value))}
                            onBlur={field.onBlur}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Status */}
                  <FormField
                    control={form.control}
                    name="status"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center gap-3 pt-6">
                        <FormControl>
                          <Switch checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                        <Label className="font-normal cursor-pointer" onClick={() => field.onChange(!field.value)}>
                          Active
                        </Label>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Start Date */}
                  <FormField
                    control={form.control}
                    name="start_date"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Start Date</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* End Date */}
                  <FormField
                    control={form.control}
                    name="end_date"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>End Date</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Description — required rich text (QM-02) */}
                <div className="space-y-2">
                  <Label>
                    Description <span className="text-destructive">*</span>
                  </Label>
                  <Controller
                    control={form.control}
                    name="description"
                    rules={{ required: "Description is required" }}
                    render={({ field }) => (
                      <RichHtmlEditor value={field.value} onChange={field.onChange} />
                    )}
                  />
                  {form.formState.errors.description && (
                    <p className="text-xs text-destructive">
                      {form.formState.errors.description.message}
                    </p>
                  )}
                </div>

                <div className="flex gap-3 pt-2">
                  <Button type="submit" disabled={mutation.isPending}>
                    {mutation.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
                    {mode === "edit" ? "Update Quarter" : "Create Quarter"}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => router.back()}>
                    Cancel
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
